/* eslint-disable prettier/prettier */
import uniq from 'lodash/uniq';
import { useReducer } from 'react';
import { useQueryClient } from 'react-query';
import { v4 as uuid } from 'uuid';

export type InvalidationState<QueryKey extends string = string> = { [dispatchId: string]: QueryKey[] };
export type InvalidationCommand<CommandType extends string = string> =
  | { commandType: CommandType; dispatchId: string }
  | { commandType: 'reset'; resetIds: string[] };

export type InvalidationReducer<CommandKey extends string = string, QueryKey extends string = string> = (
  state: InvalidationState<QueryKey>,
  command: InvalidationCommand<CommandKey>,
) => InvalidationState;

type UseInvalidations = {
  dispatchInvalidation: (action: string) => string;
  invalidateQueries: () => Promise<void>;
};

type Props = {
  invalidationReducer: InvalidationReducer;
  onReset?: (dispatchKeys: string[]) => void;
};

export const useInvalidations = ({ invalidationReducer, onReset }: Props): UseInvalidations => {
  const queryClient = useQueryClient();
  const [state, dispatchInvalidation] = useReducer(invalidationReducer, {});

  async function invalidateQueries() {
    const affectedIds = Object.keys(state);
    const queries = uniq(affectedIds.flatMap((id) => state[id]));

    const nonCommandQueryCombination = queries.filter(
      (queryKey) => !['total-premium', 'high-care-conditions'].includes(queryKey),
    );

    const shouldInvalidatePremium = queries.includes('total-premium');
    const shouldInvalidateHighCareConditions = queries.includes('high-care-conditions');

    const invalidationPromises = nonCommandQueryCombination.map((queryKey) => queryClient.invalidateQueries(queryKey));

    if (shouldInvalidatePremium) void queryClient.invalidateQueries('total-premium');
    if (shouldInvalidateHighCareConditions) void queryClient.invalidateQueries('high-care-conditions');

    await Promise.all(invalidationPromises);
    dispatchInvalidation({ commandType: 'reset', resetIds: affectedIds });
    onReset?.(affectedIds);
  }

  function safeDispatchInvalidation(commandType: string) {
    const dispatchId = uuid();
    dispatchInvalidation({ commandType, dispatchId });

    return dispatchId;
  }

  return {
    dispatchInvalidation: safeDispatchInvalidation,
    invalidateQueries,
  };
};
